/**
 * External dependencies
 */
// Require Editor CSS files.
import 'froala-editor/css/froala_editor.pkgd.min.css';
// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/code_beautifier.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_style.min.js';
import 'froala-editor/js/plugins/quick_insert.min.js';
import 'froala-editor/js/plugins/table.min.js';

import { MATH_RENDER_ENGINE_TANGERINE } from 'app/config/config';
import { addLatex } from 'core/utils/katex/config';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import FroalaEditorInput from 'react-froala-wysiwyg';
import { withTranslation } from 'react-i18next';
import { FROALA3_LECENSE_KEY, toolbarButtons, toolbarButtonsMD } from '../../../utils/froalaConfig';
import './froalaPluginMathV3.js';

import useMathLive from './useMathLive';

const TextEditor = ({ t, placeholder = t('Write here'), onModelChange = () => null, model = '', extraConfig = {}, disabled = false }) => {
  useMathLive();

  const editorRef = useRef(null);

  useEffect(() => {
    addLatex(MATH_RENDER_ENGINE_TANGERINE);
  }, []);

  // Config derivado con useMemo (no useState + useEffect): un default `extraConfig = {}`
  // crea un objeto nuevo en cada render, y un efecto que hace setConfig con esa
  // dependencia entra en bucle infinito de re-renders. Froala solo lee config al montar.
  const config = useMemo(() => ({
    key: FROALA3_LECENSE_KEY,
    events: {
      'froalaEditor.initialized': function () {},
      'froalaEditor.focus': function () {},
      'froalaEditor.paste.beforeCleanup': function () {},
      initialized() {
        const editor = this;
        editorRef.current = editor;
        disabled && editor.edit.off();
      },
    },
    fontSize: ['12', '13', '14', '15', '16', '17', '18', '20', '22', '24', '26', '28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50', '52'],
    immediateReactModelUpdate: true,
    keepFormatOnDelete: true,
    // `lineBreakerTags` define junto a qué elementos (tablas, imágenes, hr...)
    // Froala muestra su línea para insertar un párrafo. Usamos una clase que no
    // existe en el contenido para que esa línea NO aparezca nunca (mantenemos el
    // comportamiento que había). No se puede usar `[]`: Froala hace
    // `lineBreakerTags.join(',')`, y con el array vacío el selector queda como ''
    // y `element.matches('')` lanza un SyntaxError que rompe el editor. (TAN-8789)
    lineBreakerTags: ['.no-line-breaker'],
    linkAlwaysBlank: true,
    listAdvancedTypes: true,
    multiLine: true,
    pastePlain: true,
    placeholderText: placeholder,
    quickInsertEnabled: true,
    quickInsertTags: ['p', 'div'],
    tabSpaces: 4,
    toolbarButtons: toolbarButtons,
    toolbarButtonsMD: toolbarButtonsMD,
    toolbarButtonsSM: toolbarButtonsMD,
    toolbarButtonsXS: toolbarButtonsMD,
    toolbarInline: disabled,
    toolbarVisibleWithoutSelection: true,
    wordPasteModal: false,
    htmlAllowedEmptyTags: ['.MathJax_SVG svg *'],
    ...extraConfig,
  }), [disabled, extraConfig, placeholder]);

  useEffect(() => {
    const editor = editorRef.current;

    if (editor) {
      if (disabled) {
        editor.edit.off();
      } else {
        editor.edit.on();
      }
    }
  }, [disabled]);

  return (
    <div className="text-editor__container">
      <FroalaEditorInput config={config} onModelChange={onModelChange} model={model} />
    </div>
  );
};

TextEditor.propTypes = {
  disabled: PropTypes.bool,
  extraConfig: PropTypes.object,
  model: PropTypes.string,
  onModelChange: PropTypes.func,
  placeholder: PropTypes.string,
  t: PropTypes.func.isRequired,
};

export default withTranslation(['placeholder'])(TextEditor);
